// this class loads the actions associated with the building from firebase

import { useEffect, useState } from 'react';
import {
  getBuildingWorkflows,
  getProcess,
} from '@/data';
import {
  BuildingWorkflow,
  BuildingWorkflowResponse,
  Process,
} from '@urbanmix-tech/shared-js';
import { Button, CircularProgress, Menu, MenuItem, Stack, Divider } from '@mui/material';
import { useAuth } from '@/shared/context/AuthProvider';
// import Markdown from 'react-markdown';
import ConfirmationDialog from './ConfirmationDialog';
import { workflowSecretHeaders } from './WorkflowsHelper';

// Custom action that can be passed to WorkflowsProvider
export interface CustomAction {
  id: string;
  name: string;
  onClick: () => void;
}

interface WorkflowsProviderProps {
  disabled?: boolean;
  projectId?: string;
  unitId?: string;
  processId?: string;
  process?: Process;
  onWorkflowStart?: (workflow: BuildingWorkflow) => void;
  onWorkflowEnd?: (
    workflow: BuildingWorkflow,
    resonse: BuildingWorkflowResponse
  ) => void;
  variant?: 'buttons' | 'dropdown';
  customActions?: CustomAction[];
}

const { VITE_API_SERVER } = import.meta.env;

const WorkflowsProvider = ({
  disabled = false,
  projectId,
  unitId,
  processId,
  process,
  onWorkflowStart,
  onWorkflowEnd,
  variant = 'buttons',
  customActions = [],
}: WorkflowsProviderProps) => {
  const [workflows, setWorkflows] = useState<BuildingWorkflow[]>([]);
  const [runningWorkflow, setRunningWorkflow] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] =
    useState<BuildingWorkflow | null>(null);

  const { userData } = useAuth();

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const getWorkflows = async () => {
      // console.log('getting workflows projectId', projectId);
      if (process) {
        if (process.status === 'cancelled') {
          setWorkflows([]);
          return;
        }
        // If process is completed, only show active workflows (hide disabled ones)
        if (process.status === 'completed') {
          const activeWorkflows = (process.workflows ?? []).filter(
            (workflow) => workflow.status === 'active'
          );
          setWorkflows(activeWorkflows);
          return;
        }
        // console.log('I got a process in workflow provideer', process);
        setWorkflows(process.workflows ?? []);
        return;
      }
      if (!processId && projectId) {
        const result = (await getBuildingWorkflows(
          projectId
        )) as BuildingWorkflow[];

        const filtered = result.filter((workflow) => {          
          if (unitId) {            
            return workflow.parameters?.includes("unitId")
          }
          if (!unitId) {            
            return !workflow.parameters?.includes("unitId")
          }          
          return true;
        });
        // console.log('getting workflows ', result);
        setWorkflows(filtered);
      } else {
        const result = await getProcess(processId!);
        console.log('getting process ', result);
        if (result) {
          setWorkflows(result.workflows ?? []);
        }
      }
    };

    // console.log('getting workflows', projectId, processId, process);
    getWorkflows();
  }, [projectId, processId, process]);

  const startWorkflow = async (workflow: BuildingWorkflow) => {
    console.log('starting workflow', projectId, unitId, userData?.email,encodeURIComponent(VITE_API_SERVER));
    if (onWorkflowStart) onWorkflowStart(workflow);
    setRunningWorkflow(workflow.id);
    try {      
      if (workflow.redirect) {
        const separator = workflow.webhook_url.includes('?') ? '&' : '?';
        const url = `${workflow.webhook_url}${separator}uid=${userData?.id}&pid=${process?.id ?? processId}&u=${encodeURIComponent(VITE_API_SERVER)}`;
        console.log('workflow webhook', workflow.webhook_url);
        console.log('redirect to',url)
        window.open(url, '_blank');
        setRunningWorkflow(null);
        if (onWorkflowEnd)
          onWorkflowEnd(workflow, {
            message: 'redirected',
            objects: [],
            units: [],
          });
        return;
      }
      if (workflow.method === 'GET') {
        const separator = workflow.webhook_url.includes('?') ? '&' : '?';
        const url = `${workflow.webhook_url}${separator}uid=${userData?.id}&pid=${process?.id ?? processId}&u=${encodeURIComponent(VITE_API_SERVER)}`;
        const result = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...workflowSecretHeaders(),
          },
        });

        setRunningWorkflow(null);

        if (result.status !== 200) {
          if (onWorkflowEnd)
            onWorkflowEnd(workflow, {
              message: 'An error occurred',
              objects: [],
              units: [],
            });
          return;
        }
        const response: BuildingWorkflowResponse =
          (await result.json()) as BuildingWorkflowResponse;
        if (onWorkflowEnd) onWorkflowEnd(workflow, response);
      } else {
        let extraParams : Record<string,string> = {};
        workflow.parameters?.forEach((p : String) => {
          // find the first colon if there's one the left is the key the right is the value
          const index = p.indexOf(':');
          if (index > -1) {
            const key = p.substring(0, index);
            const value = p.substring(index + 1);  
            extraParams[key] = value;
          }

        });
        console.log('extraParams',workflow.parameters,extraParams);
        const result = await fetch(workflow.webhook_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...workflowSecretHeaders(),
          },
          body: JSON.stringify({
            buildingId: 1,
            projectId,
            unitId,
            processId: process?.id ?? processId,
            user: userData?.email,
            userId: userData?.id,
            userName: userData?.fullName,
            "um-server": encodeURIComponent(VITE_API_SERVER),
            ...extraParams
          }),
        });

        setRunningWorkflow(null);

        if (result.status !== 200) {
          if (onWorkflowEnd)
            onWorkflowEnd(workflow, {
              message: 'An error occurred',
              objects: [],
              units: [],
            });
          return;
        }
        const response: BuildingWorkflowResponse =
          (await result.json()) as BuildingWorkflowResponse;
        if (onWorkflowEnd) onWorkflowEnd(workflow, response);
      }
    } catch (error) {
      setRunningWorkflow(null);
      if (onWorkflowEnd)
        if (error instanceof Error) {
          onWorkflowEnd(workflow, {
            message: error.message,
            objects: [],
            units: [],
          });
        } else {
          onWorkflowEnd(workflow, {
            message: 'An error occurred',
            objects: [],
            units: [],
          });
        }
    }
  };

  const handleWorkflowClick = (workflow: BuildingWorkflow) => {
    setSelectedWorkflow(workflow);
    if (workflow.warning) {
      console.log('workflow warnings', workflow.warning);
      setConfirmationOpen(true);
      return;
    }
    startWorkflow(workflow);
  }

  const makeDropdownVariant = () => {
    return (
      <div className="flex flex-row gap-16">
        <Button
          disabled={disabled || runningWorkflow != null}
          id="basic-button"
          aria-controls="basic-menu"
          aria-haspopup="true"
          onClick={handleClick}
          variant="text"
          color="info"
        >
          <div>
          {runningWorkflow && (
                  <CircularProgress size={16} color="info" />
                )}
          Actions...
          </div>
        </Button>
        <Menu
          anchorEl={anchorEl}
          onClose={handleClose}
          open={open}
          MenuListProps={{ 'aria-labelledby': 'basic-button' }}
        >
          {/* Custom actions passed via props */}
          {customActions.map((action) => (
            <MenuItem
              key={action.id}
              onClick={() => {
                handleClose();
                action.onClick();
              }}
            >
              {action.name}
            </MenuItem>
          ))}
          {customActions.length > 0 && workflows.length > 0 && <Divider />}
          {workflows.map((workflow: BuildingWorkflow, index) => (
            <MenuItem key={`${workflow.id}-${index}`} onClick={() => {
              handleClose()
              startWorkflow(workflow)
            }}>
              {workflow.name}
            </MenuItem>
          ))}
        </Menu>
      </div>
    );
  };

  const makeButtonsVariant = () => {
    return (
      <div className="flex flex-row gap-16">
        {workflows.map((workflow: BuildingWorkflow, index) => (
          <div key={`${workflow.id}-${index}`}>
            {/* <Typography level={4}></Typography> */}
            <div className="flex-none">
              <Button
                color={workflow && workflow.name == "Cancel" ? "primary" : "primary"}
                variant="outlined"
                disabled={
                  disabled || 
                  workflow.status !== 'active' || runningWorkflow != null
                }
                onClick={() => handleWorkflowClick(workflow)}
                sx={{
                  color: '#e7e5e4 !important',
                  borderColor: 'rgba(255, 255, 255, 0.1) !important',
                  '&:hover': {
                    color: '#3b82f6 !important',
                    borderColor: '#3b82f6 !important',
                    backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
                  },
                }}
              >
                {runningWorkflow === workflow.id ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span className="animate-spin">⌛</span>
                    <span>{workflow.name}</span>
                  </Stack>
                ) : (
                  workflow.name
                )}
              </Button>
            </div>
            {/* <Markdown>{workflow.description}</Markdown> */}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-row gap-16">
      {variant === 'buttons' ? makeButtonsVariant() : makeDropdownVariant()}
      <ConfirmationDialog
        open={confirmationOpen}
        title="Workflow Confirmation"
        content={
          selectedWorkflow?.warning ||
          'Are you sure you want to start this workflow?'
        }
        onConfirm={() => {
          if (selectedWorkflow) {
            startWorkflow(selectedWorkflow);
          }
          setConfirmationOpen(false);
          setSelectedWorkflow(null);
        }}
        onCancel={() => {
          setConfirmationOpen(false);
          setSelectedWorkflow(null);
        }}
      />
    </div>
  );
};

export default WorkflowsProvider;
